import { memo } from 'react';
import { useDateNow, useReduxState } from 'hooks';
import { addDays } from 'date-fns';
import { toDate } from 'date-fns-tz';
import { Alert, Button, ModalButtonWithForm } from 'components/common';
import { Datepicker, FormGroup } from 'components/common/form';
import { editorSocket } from 'components/editor/socket';

import type { EditorHeaderSelectedBlocksProps } from './EditorHeaderSelectedBlocks';

type Props = Pick<
  EditorHeaderSelectedBlocksProps,
  'currentChapter' | 'selectedBlockIds'
>;

const EditorHeaderSelectedBlocksMarkAsNew = ({
  currentChapter,
  selectedBlockIds
}: Props) => {
  const { dateNow } = useDateNow();

  const {
    messages,
    serverConfig: { timeZone }
  } = useReduxState();

  const handleMarkAsNew = ({ expirationDate }) => {
    editorSocket.markBlocksAsNew({
      chapterId: currentChapter.id,
      blockIds: selectedBlockIds,
      expirationDate
    });
  };

  const handleRemove = (ev) => {
    handleMarkAsNew({ expirationDate: null });
    // click container to close modal
    let el = ev.target;
    while (el && el.classList && !el.classList.contains('modal-container')) {
      el = el.parentElement;
    }
    const backgroundEl = el.querySelector('.modal-container-background');
    backgroundEl.click();
  };

  const handleSubmit = (values) => {
    handleMarkAsNew({
      expirationDate: toDate(values.expirationDate, { timeZone })
    });
  };

  return (
    <ModalButtonWithForm
      buttonCategory="light"
      buttonIcon="thumbtack"
      buttonProps={{
        'aria-label': messages.editorMarkAsNew,
        'data-tooltip': messages.editorMarkAsNew
      }}
      buttonSize="small"
      buttonStyle={{ marginRight: 10 }}
      footerContent={
        <Button
          category="danger"
          onClick={handleRemove}
          style={{ float: 'left' }}
          text={messages.editorMarkAsNewRemove}
        />
      }
      modalActionCategory="success"
      modalActionText={messages.commonSave}
      modalAutoFocus={false}
      modalTitle={messages.editorMarkAsNew}
      onSubmit={handleSubmit}
      pristineIsValid={true}
    >
      {() => (
        <>
          <Alert text={messages.editorMarkAsNewInfo} />
          <FormGroup
            label={messages.commonExpirationDate}
            labelHelpText={messages.editorMarkAsNewExpiration}
          >
            <Datepicker
              field="expirationDate"
              id="editor-header-set-new-expiration-date"
              initialValue={addDays(dateNow, 1)}
              minDate={addDays(dateNow, 1)}
              placeholder={messages.commonExpirationDate}
            />
          </FormGroup>
        </>
      )}
    </ModalButtonWithForm>
  );
};

export default memo(EditorHeaderSelectedBlocksMarkAsNew);
